import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import { LayoutService } from '../../../../core/services/layout.service';
import { S3ImageUploadService } from '../../../../core/services/azure-image-upload.service';
import { CreateAndPublishStoryRequest, EmotionalTone, FamilyMember, FamilySpace } from '../../../../core/models/story.models';

// -- Types --------------------------------------------------------------------

type SharingOption  = 'public' | 'private' | 'family';
type TranscriptTab  = 'verbatim' | 'narrative';

interface StoryReviewNavState {
  videoUrl?:            string | null;
  imageUrl?:            string | null;
  mediaType?:           string;
  storyTitle?:          string;
  transcriptVerbatim?:  string;
  transcriptNarrative?: string;
  categoryId?:          number | null;
  questions?:           any[];
  questionTimeframes?:  Record<string, { questionId: string; startSecond: number; endSecond: number }>;
  videoDuration?:       number;
}

// -- Component ----------------------------------------------------------------

@Component({
  selector: 'app-story-review-video',
  templateUrl: './story-review-video.html',
  styleUrl: './story-review-video.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
})
export class StoryReviewVideo implements OnInit, AfterViewInit, OnDestroy {
  private readonly router        = inject(Router);
  private readonly platformId    = inject(PLATFORM_ID);
  private readonly api           = inject(ApiService);
  private readonly layoutService = inject(LayoutService);
  private readonly s3Upload      = inject(S3ImageUploadService);
  private readonly zone          = inject(NgZone);
  private readonly cdr           = inject(ChangeDetectorRef);

  // -- View references -------------------------------------------------------

  readonly videoRef          = viewChild<ElementRef<HTMLVideoElement>>('reviewVideo');
  readonly trimCanvas        = viewChild<ElementRef<HTMLCanvasElement>>('trimCanvas');
  readonly trimContainer     = viewChild<ElementRef<HTMLDivElement>>('trimContainer');
  readonly audioRef          = viewChild<ElementRef<HTMLAudioElement>>('reviewAudio');
  readonly audioTrimContainer = viewChild<ElementRef<HTMLDivElement>>('audioTrimContainer');

  // -- Video & media ---------------------------------------------------------

  readonly videoUrl   = signal<string | null>(null);
  readonly imageUrl   = signal<string | null>(null);
  readonly mediaType  = signal<string>('video');
  readonly storyTitle = signal('');
  readonly showReloadWarning    = signal(false);
  readonly showPublishConfirm   = signal(false);
  readonly isPublishing         = signal(false);
  readonly titleError           = signal(false);
  readonly publishError         = signal<string | null>(null);
  readonly categoryId           = signal<number>(0);
  readonly reviewQuestions      = signal<any[]>([]);
  readonly reviewTimeframes     = signal<Record<string, { questionId: string; startSecond: number; endSecond: number }>>({});
  readonly reviewVideoDuration  = signal<number>(0);

  // -- Tagging & sharing -----------------------------------------------------

  readonly familySpaces            = signal<FamilySpace[]>([]);
  readonly familyMembers           = signal<FamilyMember[]>([]);
  readonly selectedFamilySpaceId   = signal<string>('all');
  readonly selectedTaggedMemberIds = signal<string[]>([]);
  readonly sharingOption           = signal<SharingOption>('family');
  readonly selectedSharedMemberIds = signal<string[]>([]);
  readonly memberImageErrors       = signal<Record<string, boolean>>({});
  readonly selectedSharingFamilyId = signal<string>('all');

  readonly filteredFamilyMembers = computed(() => {
    const familyId = this.selectedFamilySpaceId();
    const members  = this.familyMembers();
    return familyId === 'all' ? members : members.filter(m => m.familySpaceId === familyId);
  });

  readonly filteredSharingMembers = computed(() => {
    const familyId = this.selectedSharingFamilyId();
    const members  = this.familyMembers();
    return familyId === 'all' ? members : members.filter(m => m.familySpaceId === familyId);
  });

  /** Family spaces that have at least one member — used in sharing grouped view. */
  readonly familyGroupsForSharing = computed(() =>
    this.familySpaces()
      .map(space => ({
        space,
        members: this.familyMembers().filter(m => m.familySpaceId === space.familySpaceId),
      }))
      .filter(g => g.members.length > 0),
  );

  // -- Story details ---------------------------------------------------------

  readonly storyDate     = signal(new Date().toISOString().split('T')[0]);
  readonly storyLocation = signal('');
  readonly emotionalToneId   = signal<number>(0);
  readonly emotionalTones    = signal<EmotionalTone[]>([]);

  // -- Transcript ------------------------------------------------------------

  readonly transcriptTab       = signal<TranscriptTab>('verbatim');
  readonly transcriptEditMode  = signal(false);
  readonly transcriptVerbatim  = signal('');
  readonly transcriptNarrative = signal('');

  // -- Trim (video) ----------------------------------------------------------

  readonly trimStart     = signal(0);
  readonly trimEnd       = signal(0);
  readonly videoDuration = signal(0);
  readonly trimApplied   = signal(false);

  readonly activeTranscriptText = computed(() =>
    this.transcriptTab() === 'verbatim' ? this.transcriptVerbatim() : this.transcriptNarrative(),
  );

  readonly trimStartPercent      = computed(() => this.videoDuration() > 0 ? (this.trimStart() / this.videoDuration()) * 100 : 0);
  readonly trimEndPercent        = computed(() => this.videoDuration() > 0 ? (this.trimEnd()   / this.videoDuration()) * 100 : 100);
  readonly trimSelectionWidth    = computed(() => this.trimEndPercent() - this.trimStartPercent());
  readonly trimEndExcludePercent = computed(() => 100 - this.trimEndPercent());
  readonly trimmedDuration       = computed(() => Math.max(0, this.trimEnd()       - this.trimStart()));
  readonly removedDuration       = computed(() => Math.max(0, this.videoDuration() - this.trimmedDuration()));

  private draggingHandle: 'start' | 'end' | null = null;
  private readonly boundDragMove = this.onDragMove.bind(this);
  private readonly boundDragEnd  = this.onDragEnd.bind(this);

  // -- Trim (audio) ----------------------------------------------------------

  readonly audioTrimStart         = signal(0);
  readonly audioTrimEnd           = signal(0);
  readonly audioDuration          = signal(0);
  readonly audioTrimApplied       = signal(false);

  readonly audioTrimStartPercent      = computed(() => this.audioDuration() > 0 ? (this.audioTrimStart() / this.audioDuration()) * 100 : 0);
  readonly audioTrimEndPercent        = computed(() => this.audioDuration() > 0 ? (this.audioTrimEnd()   / this.audioDuration()) * 100 : 100);
  readonly audioTrimSelectionWidth    = computed(() => this.audioTrimEndPercent() - this.audioTrimStartPercent());
  readonly audioTrimEndExcludePercent = computed(() => 100 - this.audioTrimEndPercent());
  readonly audioTrimmedDuration       = computed(() => Math.max(0, this.audioTrimEnd()    - this.audioTrimStart()));
  readonly audioRemovedDuration       = computed(() => Math.max(0, this.audioDuration()   - this.audioTrimmedDuration()));

  private draggingAudioHandle: 'start' | 'end' | null = null;
  private readonly boundAudioDragMove = this.onAudioDragMove.bind(this);
  private readonly boundAudioDragEnd  = this.onAudioDragEnd.bind(this);

  // -- Thumbnail -------------------------------------------------------------

  readonly thumbnailPreviewUrl = signal<string | null>(null);
  readonly thumbnailFileName   = signal<string | null>(null);
  readonly thumbnailFileSize   = signal<string>('');
  readonly thumbnailError      = signal(false);
  private thumbnailFile: File | null = null;

  // -- Audio playback --------------------------------------------------------

  readonly isAudioPlaying = signal(false);

  // -- Lifecycle -------------------------------------------------------------

  private readonly onBeforeUnload = (e: BeforeUnloadEvent): void => { e.preventDefault(); };
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    const isRefresh = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && e.key === 'r');
    if (isRefresh) { e.preventDefault(); this.showReloadWarning.set(true); }
  };

  async ngOnInit(): Promise<void> {
    window.addEventListener('beforeunload', this.onBeforeUnload);
    window.addEventListener('keydown', this.onKeyDown);
    this.layoutService.setHideHeader(true);

    // Detect page refresh: check for our navigation flag set by story-record.
    // On a real refresh, Angular doesn't re-run navigateToReview(), so the flag is absent.
    if (isPlatformBrowser(this.platformId)) {
      const wasNavigated = sessionStorage.getItem('fm_story_review_navigated');
      sessionStorage.removeItem('fm_story_review_navigated'); // consume immediately
      if (!wasNavigated) {
        sessionStorage.removeItem('fm_story_review_video_url');
        sessionStorage.removeItem('fm_story_record_questions');
        this.router.navigate(['/app/stories/record'], { replaceUrl: true });
        return;
      }
    }

    const navState       = (this.router.getCurrentNavigation()?.extras.state ?? history.state ?? {}) as StoryReviewNavState;
    const urlFromState   = navState.videoUrl ?? null;
    const titleFromState = navState.storyTitle ?? '';

    // title starts empty — required field
    if (navState.mediaType)           this.mediaType.set(navState.mediaType);
    if (navState.imageUrl)            this.imageUrl.set(navState.imageUrl);
    if (navState.categoryId)          this.categoryId.set(navState.categoryId);
    if (navState.questions?.length)   this.reviewQuestions.set(navState.questions);
    if (navState.questionTimeframes)  this.reviewTimeframes.set(navState.questionTimeframes);
    if (navState.videoDuration)       this.reviewVideoDuration.set(navState.videoDuration);

    // -- Transcripts: navState → sessionStorage → default placeholder -------
    let verbatimText  = navState.transcriptVerbatim  || '';
    let narrativeText = navState.transcriptNarrative || '';
    if (isPlatformBrowser(this.platformId)) {
      if (!verbatimText)  verbatimText  = sessionStorage.getItem('fm_story_review_transcript_verbatim')  ?? '';
      if (!narrativeText) narrativeText = sessionStorage.getItem('fm_story_review_transcript_narrative') ?? '';
    }
    if (!verbatimText)  verbatimText  = 'I remember this moment vividly. This auto-generated transcript can be edited before publishing.';
    if (!narrativeText) narrativeText = verbatimText;
    this.transcriptVerbatim.set(verbatimText);
    this.transcriptNarrative.set(narrativeText);

    if (urlFromState) {
      // Use URL from navigation state (blob or real URL — both valid during normal nav)
      this.videoUrl.set(urlFromState);
      if (isPlatformBrowser(this.platformId)) {
        sessionStorage.setItem('fm_story_review_video_url', urlFromState);
      }
    } else if (isPlatformBrowser(this.platformId)) {
      const cachedUrl = sessionStorage.getItem('fm_story_review_video_url');
      if (!cachedUrl) {
        // No URL available at all — redirect to step 1
        sessionStorage.removeItem('fm_story_review_video_url');
        sessionStorage.removeItem('fm_story_record_questions');
        this.router.navigate(['/app/stories/record'], { replaceUrl: true });
        return;
      }
      this.videoUrl.set(cachedUrl);
    }

    // Restore image URL for image-audio type
    if (!this.imageUrl() && isPlatformBrowser(this.platformId)) {
      const cachedImage = sessionStorage.getItem('fm_story_review_image_url');
      if (cachedImage) this.imageUrl.set(cachedImage);
    }

    await this.loadTaggingData();
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Fallback: video may have loaded before event listeners were attached
      setTimeout(() => {
        const video = this.videoRef()?.nativeElement;
        if (video && video.readyState >= 1) this.resolveDuration(video);
      }, 200);
    }
  }

  dismissReloadWarning(): void { this.showReloadWarning.set(false); }

  confirmReload(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
    location.reload();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
    this.layoutService.setHideHeader(false);
    this.onDragEnd();
    this.onAudioDragEnd();
  }

  // -- Video metadata & trim init --------------------------------------------

  onVideoMetadataLoaded(): void {
    const video = this.videoRef()?.nativeElement;
    if (video) this.resolveDuration(video);
  }

  /**
   * MediaRecorder WebM blobs have duration = Infinity (no duration header).
   * Workaround: seek to a huge time; the browser clamps to the real end,
   * then we read currentTime from the seeked event.
   */
  private resolveDuration(video: HTMLVideoElement): void {
    if (isFinite(video.duration) && video.duration > 0) {
      this.initTrimFromDuration(video.duration);
      return;
    }
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked);
      const realDuration = video.currentTime;
      video.currentTime  = 0;
      if (realDuration > 0) this.initTrimFromDuration(realDuration);
    };
    video.addEventListener('seeked', onSeeked);
    video.currentTime = 1e101; // browser clamps to actual end
  }

  private initTrimFromDuration(rawDuration: number): void {
    if (!isFinite(rawDuration) || rawDuration <= 0) return;
    const duration = parseFloat(rawDuration.toFixed(3));
    if (this.videoDuration() === duration) return;
    this.videoDuration.set(duration);
    this.trimStart.set(0);
    this.trimEnd.set(duration);
    this.cdr.markForCheck();
    setTimeout(() => this.generateTrimThumbnails(), 50);
  }

  // -- Trim controls ---------------------------------------------------------

  applyTrim(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video || this.trimEnd() <= this.trimStart()) return;
    this.trimApplied.set(true);
    video.currentTime = this.trimStart();
    video.play().catch(() => {});
  }

  resetTrim(): void {
    this.trimApplied.set(false);
    this.trimStart.set(0);
    this.trimEnd.set(this.videoDuration());
    const video = this.videoRef()?.nativeElement;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }

  onVideoTimeUpdate(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video) return;
    if (this.trimApplied()) {
      if (video.currentTime < this.trimStart()) {
        video.currentTime = this.trimStart();
      } else if (video.currentTime >= this.trimEnd()) {
        video.pause();
        video.currentTime = this.trimStart(); // loop back to trim start
      }
    }
  }

    onVideoSeeked(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video || !this.trimApplied()) return;
    if (video.currentTime < this.trimStart()) {
      video.currentTime = this.trimStart();
    } else if (video.currentTime > this.trimEnd()) {
      video.currentTime = this.trimEnd();
    }
  }

  // -- Trim thumbnail strip --------------------------------------------------

  generateTrimThumbnails(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const url      = this.videoUrl();
    const duration = this.videoDuration();
    const canvas   = this.trimCanvas()?.nativeElement;
    if (!url || !duration || !canvas) return;

    const THUMB_COUNT = 12;
    const THUMB_H     = 56;
    const THUMB_W     = Math.round(THUMB_H * (16 / 9));
    canvas.width  = THUMB_COUNT * THUMB_W;
    canvas.height = THUMB_H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const vid = document.createElement('video');
    vid.src     = url;
    vid.muted   = true;
    vid.preload = 'auto';
    (vid as HTMLVideoElement & { playsInline: boolean }).playsInline = true;
    // Do NOT set crossOrigin for blob URLs — it taints the canvas.

    let index = 0;
    const captureNext = () => {
      if (index >= THUMB_COUNT) { this.cdr.markForCheck(); return; }
      vid.currentTime = index === 0 ? 0.01 : (index / (THUMB_COUNT - 1)) * duration;
    };

    vid.addEventListener('seeked',         () => { try { ctx.drawImage(vid, index * THUMB_W, 0, THUMB_W, THUMB_H); } catch (_) {} index++; captureNext(); });
    vid.addEventListener('loadedmetadata', captureNext);
    vid.addEventListener('error',          () => this.cdr.markForCheck());
    vid.load();
  }

  // -- Drag handles ----------------------------------------------------------

  startDragging(handle: 'start' | 'end', event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.draggingHandle = handle;
    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.boundDragMove);
      document.addEventListener('mouseup',   this.boundDragEnd);
      document.addEventListener('touchmove', this.boundDragMove, { passive: false });
      document.addEventListener('touchend',  this.boundDragEnd);
    });
  }

  onDragMove(event: MouseEvent | TouchEvent): void {
    if (!this.draggingHandle) return;
    const container = this.trimContainer()?.nativeElement;
    if (!container) return;
    const rect    = container.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const ratio   = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const dur     = this.videoDuration();
    if (!dur) return;
    const time = parseFloat((ratio * dur).toFixed(3));
    this.zone.run(() => {
      if (this.draggingHandle === 'start') {
        this.trimStart.set(Math.min(time, Math.max(0, this.trimEnd() - 0.5)));
      } else {
        this.trimEnd.set(Math.max(this.trimStart() + 0.5, Math.min(time, dur)));
      }
      this.trimApplied.set(false);
      this.cdr.markForCheck();
    });
  }

  onDragEnd(): void {
    this.draggingHandle = null;
    document.removeEventListener('mousemove', this.boundDragMove);
    document.removeEventListener('mouseup',   this.boundDragEnd);
    document.removeEventListener('touchmove', this.boundDragMove);
    document.removeEventListener('touchend',  this.boundDragEnd);
  }

  // -- Audio metadata & trim -------------------------------------------------

  onAudioMetadataLoaded(): void {
    const audio = this.audioRef()?.nativeElement;
    if (audio) this.resolveAudioDuration(audio);
  }

  private resolveAudioDuration(audio: HTMLAudioElement): void {
    if (isFinite(audio.duration) && audio.duration > 0) {
      this.initAudioTrimFromDuration(audio.duration);
      return;
    }
    const onSeeked = () => {
      audio.removeEventListener('seeked', onSeeked);
      const realDuration = audio.currentTime;
      audio.currentTime  = 0;
      if (realDuration > 0) this.initAudioTrimFromDuration(realDuration);
    };
    audio.addEventListener('seeked', onSeeked);
    audio.currentTime = 1e101;
  }

  private initAudioTrimFromDuration(rawDuration: number): void {
    if (!isFinite(rawDuration) || rawDuration <= 0) return;
    const duration = parseFloat(rawDuration.toFixed(3));
    if (this.audioDuration() === duration) return;
    this.audioDuration.set(duration);
    this.audioTrimStart.set(0);
    this.audioTrimEnd.set(duration);
    this.cdr.markForCheck();
  }

  applyAudioTrim(): void {
    const audio = this.audioRef()?.nativeElement;
    if (!audio || this.audioTrimEnd() <= this.audioTrimStart()) return;
    this.audioTrimApplied.set(true);
    audio.currentTime = this.audioTrimStart();
    audio.play().catch(() => {});
  }

  resetAudioTrim(): void {
    this.audioTrimApplied.set(false);
    this.audioTrimStart.set(0);
    this.audioTrimEnd.set(this.audioDuration());
    const audio = this.audioRef()?.nativeElement;
    if (audio) { audio.pause(); audio.currentTime = 0; }
  }

  onAudioTimeUpdate(): void {
    const audio = this.audioRef()?.nativeElement;
    if (!audio || !this.audioTrimApplied()) return;
    if (audio.currentTime < this.audioTrimStart()) {
      audio.currentTime = this.audioTrimStart();
    } else if (audio.currentTime >= this.audioTrimEnd()) {
      audio.pause();
      audio.currentTime = this.audioTrimStart();
    }
  }

  onAudioPlay():  void { this.isAudioPlaying.set(true);  this.cdr.markForCheck(); }
  onAudioPause(): void { this.isAudioPlaying.set(false); this.cdr.markForCheck(); }

  startAudioDragging(handle: 'start' | 'end', event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.draggingAudioHandle = handle;
    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.boundAudioDragMove);
      document.addEventListener('mouseup',   this.boundAudioDragEnd);
      document.addEventListener('touchmove', this.boundAudioDragMove, { passive: false });
      document.addEventListener('touchend',  this.boundAudioDragEnd);
    });
  }

  onAudioDragMove(event: MouseEvent | TouchEvent): void {
    if (!this.draggingAudioHandle) return;
    const container = this.audioTrimContainer()?.nativeElement;
    if (!container) return;
    const rect    = container.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const ratio   = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const dur     = this.audioDuration();
    if (!dur) return;
    const time = parseFloat((ratio * dur).toFixed(3));
    this.zone.run(() => {
      if (this.draggingAudioHandle === 'start') {
        this.audioTrimStart.set(Math.min(time, Math.max(0, this.audioTrimEnd() - 0.5)));
      } else {
        this.audioTrimEnd.set(Math.max(this.audioTrimStart() + 0.5, Math.min(time, dur)));
      }
      this.audioTrimApplied.set(false);
      this.cdr.markForCheck();
    });
  }

  onAudioDragEnd(): void {
    this.draggingAudioHandle = null;
    document.removeEventListener('mousemove', this.boundAudioDragMove);
    document.removeEventListener('mouseup',   this.boundAudioDragEnd);
    document.removeEventListener('touchmove', this.boundAudioDragMove);
    document.removeEventListener('touchend',  this.boundAudioDragEnd);
  }

  // -- Thumbnail -------------------------------------------------------------

  onThumbnailUpload(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.thumbnailFile = file;
    this.thumbnailFileName.set(file.name);
    this.thumbnailFileSize.set(`${(file.size / 1024).toFixed(0)} KB`);
    this.thumbnailError.set(false);
    const reader = new FileReader();
    reader.onload = (e) => {
      this.thumbnailPreviewUrl.set(e.target?.result as string);
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  removeThumbnail(): void {
    this.thumbnailFile        = null;
    this.thumbnailPreviewUrl.set(null);
    this.thumbnailFileName.set(null);
    this.thumbnailFileSize.set('');
  }

  // -- Tagging ---------------------------------------------------------------

  readonly collapsedTagFamilyIds = signal<string[]>([]);

  toggleTagFamily(familySpaceId: string): void {
    this.collapsedTagFamilyIds.update(ids =>
      ids.includes(familySpaceId) ? ids.filter(id => id !== familySpaceId) : [...ids, familySpaceId],
    );
  }

  isTagFamilyExpanded(familySpaceId: string): boolean {
    return !this.collapsedTagFamilyIds().includes(familySpaceId);
  }

  selectFamily(familySpaceId: string): void { this.selectedFamilySpaceId.set(familySpaceId); }

  selectSharingFamily(familySpaceId: string): void { this.selectedSharingFamilyId.set(familySpaceId); }

  toggleTaggedMember(memberId: string): void {
    this.selectedTaggedMemberIds.update(ids =>
      ids.includes(memberId) ? ids.filter(id => id !== memberId) : [...ids, memberId],
    );
  }

  isTagged(memberId: string): boolean { return this.selectedTaggedMemberIds().includes(memberId); }

  // -- Sharing ---------------------------------------------------------------

  setSharingOption(option: SharingOption): void { this.sharingOption.set(option); }

  toggleSharedMember(memberId: string): void {
    this.selectedSharedMemberIds.update(ids =>
      ids.includes(memberId) ? ids.filter(id => id !== memberId) : [...ids, memberId],
    );
  }

  isShared(memberId: string): boolean { return this.selectedSharedMemberIds().includes(memberId); }

  // -- Story details ---------------------------------------------------------

  onToneChange(event: Event):    void { this.emotionalToneId.set(Number((event.target as HTMLSelectElement).value)); }

  // -- Transcript ------------------------------------------------------------

  setTranscriptTab(tab: TranscriptTab): void { this.transcriptTab.set(tab); }

  toggleTranscriptEdit(): void { this.transcriptEditMode.update(v => !v); }

  onTranscriptInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    if (this.transcriptTab() === 'verbatim') this.transcriptVerbatim.set(value);
    else                                      this.transcriptNarrative.set(value);
  }

  // -- Member helpers --------------------------------------------------------

  getMemberPhotoUrl(member: FamilyMember): string { return member.image?.trim() || ''; }

  hasMemberImage(member: FamilyMember): boolean {
    return !!this.getMemberPhotoUrl(member) && !this.memberImageErrors()[member.familyMemberId];
  }

  onMemberImageError(memberId: string): void {
    this.memberImageErrors.update(map => ({ ...map, [memberId]: true }));
  }

  getMemberInitials(member: FamilyMember): string {
    const parts = (member.fullName || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.charAt(0) ?? '';
    const last  = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) ?? '' : '';
    return (first + last).toUpperCase() || 'NA';
  }

  /** Returns the family name, looking it up from familySpaces if the member field is blank. */
  getFamilyName(member: FamilyMember): string {
    if (member.familyName?.trim()) return member.familyName;
    return this.familySpaces().find(f => f.familySpaceId === member.familySpaceId)?.familyName ?? '';
  }

  // -- Formatting ------------------------------------------------------------

  formatRecordingTime(seconds: number): string {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return '00:00';
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  // -- Navigation ------------------------------------------------------------

  backToRecording(): void {
    this.router.navigate(['/app/stories/record'], { state: { returnFromReview: true } });
  }

  openPublishConfirm(): void {
    if (!this.storyTitle().trim()) {
      this.titleError.set(true);
      const el = document.getElementById('storyTitleInput');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.focus();
      return;
    }
    this.titleError.set(false);

    const isAudioType = this.mediaType() === 'audio' || this.mediaType() === 'image-audio';
    if (isAudioType && !this.thumbnailPreviewUrl()) {
      this.thumbnailError.set(true);
      const el = document.getElementById('fmReviewThumbnail');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    this.thumbnailError.set(false);
    this.showPublishConfirm.set(true);
  }

  cancelPublish(): void { this.showPublishConfirm.set(false); }

  async saveAndPublish(): Promise<void> {
    this.showPublishConfirm.set(false);
    this.isPublishing.set(true);
    this.publishError.set(null);

    try {
      // -- Upload video/audio to S3 before creating the story ---------------
      const blobUrl = this.videoUrl() ?? '';
      let mediaFileName = '';
      if (blobUrl.startsWith('blob:')) {
        mediaFileName = await this.s3Upload.uploadStoryMedia(blobUrl, this.mediaType());
      } else {
        // Already a real URL or filename — extract just the filename
        mediaFileName = blobUrl.split('/').pop() ?? blobUrl;
      }

      // -- Upload thumbnail (audio / image-audio types) if provided ---------
      let thumbnailFileName: string | undefined;
      if (this.thumbnailFile) {
        const thumbBlobUrl = URL.createObjectURL(this.thumbnailFile);
        try {
          thumbnailFileName = await this.s3Upload.uploadStoryMedia(thumbBlobUrl, 'image/jpeg');
        } finally {
          URL.revokeObjectURL(thumbBlobUrl);
        }
      }

      const mediaTypeMap: Record<string, number> = { video: 1, audio: 2, 'image-audio': 3 };
      const mediaTypeNum = mediaTypeMap[this.mediaType().toLowerCase()] ?? 1;

      const sharingMap: Record<string, number> = { private: 1, family: 3, public: 3 };
      let visibilityTypeId = sharingMap[this.sharingOption()] ?? 3;
      if (this.sharingOption() === 'family' && this.selectedSharedMemberIds().length > 0) {
        visibilityTypeId = 5; // Selected Members
      }

      const questions = this.reviewQuestions().map((q, idx) => {
        const tf = this.reviewTimeframes()[q.id];
        return {
          questionId:     q.id,
          questionSource: q.sourceType ?? 1,
          questionOrder:  idx + 1,
          startTime:      tf?.startSecond ?? 0,
          endTime:        tf?.endSecond   ?? 0,
        };
      });

      const isAudioType = this.mediaType() === 'audio' || this.mediaType() === 'image-audio';
      const effectiveTrimStart = isAudioType
        ? (this.audioTrimApplied() ? this.audioTrimStart() : 0)
        : (this.trimApplied()      ? this.trimStart()      : 0);
      const effectiveTrimEnd = isAudioType
        ? (this.audioTrimApplied() ? this.audioTrimEnd()   : (this.audioDuration() || this.reviewVideoDuration()))
        : (this.trimApplied()      ? this.trimEnd()        : (this.reviewVideoDuration() || this.videoDuration()));

      const payload: CreateAndPublishStoryRequest = {
        categoryId:             this.categoryId(),
        mediaType:              mediaTypeNum,
        questions,
        title:                  this.storyTitle().trim(),
        mediaFileName,
        thumbnailFileName,
        duration:               this.reviewVideoDuration() || (isAudioType ? this.audioDuration() : this.videoDuration()),
        storyDate:              this.storyDate() || new Date().toISOString().split('T')[0],
        location:               this.storyLocation(),
        emotionalToneId:        this.emotionalToneId(),
        visibilityTypeId,
        trimStart:              effectiveTrimStart,
        trimEnd:                effectiveTrimEnd,
        taggedFamilyMemberIds:  this.selectedTaggedMemberIds(),
        sharedWithUserIds:      this.sharingOption() === 'public' ? [] : this.selectedSharedMemberIds(),
      };

      const res = await this.api.request<{ storyId: string }>(
        'POST', API_ROUTES.CREATE_AND_PUBLISH_STORY, payload,
        { showLoader: true, showToaster: true, useToken: true },
      );

      if (res.status) {
        // Clean up session storage
        sessionStorage.removeItem('fm_story_review_video_url');
        sessionStorage.removeItem('fm_story_record_questions');
        sessionStorage.removeItem('fm_story_review_transcript_verbatim');
        sessionStorage.removeItem('fm_story_review_transcript_narrative');
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        window.removeEventListener('keydown', this.onKeyDown);
        this.router.navigate(['/app/stories']);
      } else {
        this.publishError.set(res.message ?? 'Failed to publish story. Please try again.');
        this.isPublishing.set(false);
      }
    } catch (err) {
      this.publishError.set('An unexpected error occurred. Please try again.');
      this.isPublishing.set(false);
    }
  }

  // -- Private: data loading -------------------------------------------------

  private async loadTaggingData(): Promise<void> {
    const [familyRes, memberRes, toneRes] = await Promise.all([
      this.api.request<FamilySpace[]>   ('GET', API_ROUTES.GET_ACTIVE_FAMILY_LIST, undefined, { showLoader: false, showToaster: false }),
      this.api.request<FamilyMember[]>  ('GET', API_ROUTES.GET_FAMILY_MEMBER_LIST, undefined, { showLoader: false, showToaster: false }),
      this.api.request<EmotionalTone[]> ('GET', API_ROUTES.GET_EMOTIONAL_TONES,    undefined, { showLoader: false, showToaster: false }),
    ]);
    if (familyRes.status && familyRes.data) this.familySpaces.set(familyRes.data);
    if (memberRes.status && memberRes.data) {
      this.familyMembers.set(memberRes.data);
      this.selectedSharedMemberIds.set(memberRes.data.slice(0, 3).map(m => m.familyMemberId));
    }
    if (toneRes.status && toneRes.data) this.emotionalTones.set(toneRes.data);
  }
}